import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getAppBySlug } from '../data/appsData';

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold mb-4 text-white border-b border-white/10 pb-3">{title}</h2>
    <div className="text-gray-400 space-y-3 leading-relaxed">{children}</div>
  </section>
);

const AppPrivacyPage = () => {
  const { appSlug } = useParams();
  const app = getAppBySlug(appSlug);

  if (!app) return <Navigate to="/legal" replace />;
  if (app.customPrivacyPath) return <Navigate to={app.customPrivacyPath} replace />;

  const updated = 'June 28, 2026';
  const effective = 'June 28, 2026';

  return (
    <div className="min-h-screen pt-24 pb-20">
      {/* Header */}
      <div
        className="py-14 relative"
        style={{ background: `linear-gradient(135deg, ${app.accentColor}22 0%, transparent 70%)` }}
      >
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/legal" className="text-sm text-gray-500 hover:text-white mb-6 inline-flex items-center gap-2 transition-colors">
            ← Back to Legal Hub
          </Link>
          <div className="flex items-center gap-4 mt-4">
            <span className="text-5xl">{app.icon}</span>
            <div>
              <p className="text-xs font-bold tracking-widest uppercase mb-1" style={{ color: app.accentColor }}>
                Privacy Policy
              </p>
              <h1 className="text-3xl md:text-4xl font-bold text-white">{app.name}</h1>
              <p className="text-gray-500 text-sm mt-1">{app.packageName} · {app.category}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 mt-6 text-xs text-gray-500">
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">Effective: {effective}</span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">Last updated: {updated}</span>
            <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10">Platform: Google Play (Android)</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Intro */}
        <div
          className="p-6 rounded-2xl mb-10 border"
          style={{ background: `${app.accentColor}10`, borderColor: `${app.accentColor}30` }}
        >
          <p className="text-gray-300 leading-relaxed">
            Riven Byte Studios ("we", "us", or "our") operates the <strong className="text-white">{app.name}</strong> mobile
            application (the "App"). This Privacy Policy explains how we handle information when you use the App. We are
            committed to protecting your privacy and being transparent about our practices.
          </p>
        </div>

        <Section title="1. Information We Collect">
          {app.collectsData && app.dataTypes.length > 0 ? (
            <>
              <p>The App stores the following information <strong className="text-white">locally on your device only</strong>:</p>
              <ul className="list-disc list-inside space-y-1 mt-2">
                {app.dataTypes.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
              <p className="mt-3">This data never leaves your device and is not transmitted to our servers.</p>
            </>
          ) : (
            <p>
              <strong className="text-white">We do not collect any personal information.</strong> The App does not require
              account registration, does not collect your name, email, location, or any other personal data. No data is
              transmitted to Riven Byte Studios servers.
            </p>
          )}
        </Section>

        <Section title="2. Automatically Collected Data">
          <p>
            The App does not directly collect usage analytics or crash reports. However, third-party services integrated
            into the App may automatically collect certain non-personal technical information as described in Section 3.
          </p>
        </Section>

        {app.usesAdMob && (
          <Section title="3. Advertising (Google AdMob)">
            <p>
              The App uses <strong className="text-white">Google AdMob</strong> to display advertisements. AdMob may collect
              and use data to serve personalized or non-personalized ads, including:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-1">
              <li>Device advertising ID (Android Advertising ID)</li>
              <li>IP address and general location (country/region)</li>
              <li>Device type, operating system, and language</li>
              <li>App usage and interaction data for ad relevance</li>
            </ul>
            <p className="mt-4">
              You can opt out of personalized advertising in your device settings under{' '}
              <strong className="text-white">Settings → Google → Ads → Opt out of Ads Personalization</strong>.
            </p>
            <p className="mt-3">
              Google AdMob's privacy policy:{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
                style={{ color: app.accentColor }}
              >
                https://policies.google.com/privacy
              </a>
            </p>
          </Section>
        )}

        <Section title={`${app.usesAdMob ? '4' : '3'}. Third-Party Services`}>
          <p>The App integrates the following third-party SDKs and services:</p>
          <ul className="list-disc list-inside mt-3 space-y-1">
            {app.sdks.map((sdk, i) => <li key={i}>{sdk}</li>)}
          </ul>
          <p className="mt-4">
            Each third-party service operates under its own privacy policy. We encourage you to review their policies
            for details on their data practices.
          </p>
        </Section>

        <Section title={`${app.usesAdMob ? '5' : '4'}. Children's Privacy`}>
          <p>
            The App is not directed to children under 13. We do not knowingly collect personal information from children
            under 13. If you are a parent or guardian and believe your child has provided personal information, please
            contact us at{' '}
            <a href={`mailto:${app.contact}`} className="underline" style={{ color: app.accentColor }}>
              {app.contact}
            </a>{' '}
            and we will promptly delete it.
          </p>
        </Section>

        <Section title={`${app.usesAdMob ? '6' : '5'}. Data Security`}>
          <p>
            We implement reasonable security measures to protect any information associated with the App. Since the App
            does not transmit personal data to our servers, your risk exposure is limited to the third-party services
            listed above, each of which maintains their own security standards.
          </p>
        </Section>

        <Section title={`${app.usesAdMob ? '7' : '6'}. Your Rights`}>
          <p>Depending on your location, you may have the right to:</p>
          <ul className="list-disc list-inside mt-3 space-y-1">
            <li>Access, correct, or delete any personal data held about you</li>
            <li>Opt out of personalized advertising (via device settings)</li>
            <li>Request information about what data third-party services hold about you</li>
            <li>Lodge a complaint with your local data protection authority</li>
          </ul>
        </Section>

        <Section title={`${app.usesAdMob ? '8' : '7'}. Changes to This Policy`}>
          <p>
            We may update this Privacy Policy from time to time. Changes are effective immediately upon posting. The
            "Last updated" date at the top of this page reflects the most recent revision. Continued use of the App after
            changes constitutes acceptance of the updated policy.
          </p>
        </Section>

        <Section title={`${app.usesAdMob ? '9' : '8'}. Contact Us`}>
          <p>
            If you have any questions about this Privacy Policy, please contact us:
          </p>
          <div className="mt-4 p-5 rounded-xl bg-white/5 border border-white/10 space-y-2">
            <p><span className="text-gray-500">Studio:</span> <span className="text-white">Riven Byte Studios</span></p>
            <p><span className="text-gray-500">Email:</span>{' '}
              <a href={`mailto:${app.contact}`} className="underline" style={{ color: app.accentColor }}>
                {app.contact}
              </a>
            </p>
            <p><span className="text-gray-500">Website:</span>{' '}
              <a href="https://riven-byte.pages.dev" className="underline" style={{ color: app.accentColor }}>
                riven-byte.pages.dev
              </a>
            </p>
            <p><span className="text-gray-500">Location:</span> <span className="text-gray-300">Dar es Salaam, Tanzania</span></p>
          </div>
        </Section>

        {/* Nav to Terms */}
        <div className="mt-12 p-6 rounded-2xl bg-white/4 border border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-gray-400 text-sm">Also read our Terms of Service for this app</p>
            <p className="text-xs text-gray-600 mt-0.5">Your continued use implies acceptance of both documents</p>
          </div>
          <Link
            to={`/apps/${app.slug}/terms`}
            className="px-5 py-2.5 rounded-xl font-medium text-sm text-black flex-shrink-0"
            style={{ background: app.accentColor }}
          >
            Terms of Service →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AppPrivacyPage;
